/**
 * Browser replacements for the Tauri file dialog / filesystem plugins.
 *
 * The desktop app called `open()` from `@tauri-apps/plugin-dialog` to get an absolute path, and
 * `save()` + `writeTextFile` to write one. Neither concept exists in the browser: the user hands us
 * a `File` and we hand them back a download. These helpers keep those interactions imperative so
 * the existing call sites read the same way.
 */

export interface PickFileOptions {
  /** An `accept` attribute value, e.g. `'.csv,.txt'` or `'audio/*'`. */
  accept?: string;
  multiple?: boolean;
}

/** Audio formats the desktop app's file dialog accepted. */
export const AUDIO_ACCEPT = '.aif,.aiff,.ape,.flac,.ogg,.aac,.mp2,.mp3,.wav,.wma,.m4a,audio/*';

/** Opens the OS file picker and resolves with the chosen files, or `null` if cancelled. */
export function pickFiles(options: PickFileOptions = {}): Promise<File[] | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (options.accept) {
      input.accept = options.accept;
    }
    input.multiple = Boolean(options.multiple);
    input.style.display = 'none';

    let settled = false;
    const settle = (value: File[] | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.addEventListener('change', () => {
      const files = input.files ? Array.from(input.files) : [];
      settle(files.length > 0 ? files : null);
    });

    // `cancel` fires in modern browsers when the picker is dismissed. Without it the promise
    // would never settle, leaking a pending await on every cancelled pick.
    input.addEventListener('cancel', () => settle(null));

    document.body.appendChild(input);
    input.click();
  });
}

/** Single-file convenience wrapper around {@link pickFiles}. */
export async function pickFile(options: Omit<PickFileOptions, 'multiple'> = {}): Promise<File | null> {
  const files = await pickFiles({...options, multiple: false});
  return files?.[0] ?? null;
}

/** Triggers a browser download for an already-built Blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Give the click a tick to be handled before revoking the object URL.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** Triggers a browser download for a string payload. Replaces Tauri's `writeTextFile`. */
export function downloadText(text: string, filename: string, mimeType = 'text/plain;charset=utf-8'): void {
  downloadBlob(new Blob([text], {type: mimeType}), filename);
}

/** Reads a picked file as text. Replaces Tauri's `readTextFile`. */
export function readFileAsText(file: File): Promise<string> {
  return file.text();
}
